import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/apiFetch';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ThroughputPunto, ApiResponse } from '../types';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const rangos = [
    { dias: 7, label: '7d' },
    { dias: 30, label: '30d' },
    { dias: 90, label: '90d' },
];

export const ThroughputHistoryChart: React.FC = () => {
    const [dias, setDias] = useState<number>(30);
    const [data, setData] = useState<ThroughputPunto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await apiFetch(`${API_BASE}/tareas/throughput-history?dias=${dias}`);
                const json: ApiResponse<ThroughputPunto[]> = await res.json();
                if (json.success && json.data) setData(json.data);
                else setError(json.error || 'Error al cargar histórico');
            } catch (e) {
                setError('No se pudo conectar con el servidor');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [dias]);

    const total = data.reduce((acc, d) => acc + d.completadas, 0);
    const promedio = data.length > 0 ? (total / data.length).toFixed(1) : '0';

    return (
        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-base font-semibold text-slate-800">Throughput Histórico</h3>
                    <p className="text-xs text-slate-500">
                        Total: <span className="font-semibold text-emerald-600">{total}</span> ·
                        Promedio: <span className="font-semibold text-slate-700">{promedio}/día</span>
                    </p>
                </div>
                <div className="flex gap-1">
                    {rangos.map(r => (
                        <button
                            key={r.dias}
                            onClick={() => setDias(r.dias)}
                            className={`px-3 py-1 text-xs rounded transition-colors ${
                                dias === r.dias
                                    ? 'bg-slate-800 text-white'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>
            {loading && <div className="h-48 flex items-center justify-center text-slate-400 text-sm">Cargando...</div>}
            {error && <div className="h-48 flex items-center justify-center text-red-500 text-sm">{error}</div>}
            {!loading && !error && data.length > 0 && (
                <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="fecha" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={(v: string) => v.slice(5)} />
                        <YAxis tick={{ fontSize: 10, fill: '#64748b' }} allowDecimals={false} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0' }} />
                        <Bar dataKey="completadas" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            )}
            {!loading && !error && data.length === 0 && (
                <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                    Sin tareas completadas en este período.
                </div>
            )}
        </div>
    );
};
